import { motion } from 'framer-motion';
import { Instagram, Mail, Phone, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const socialLinks = [
    {
      icon: Instagram,
      href: 'https://instagram.com/agency.loom',
      label: 'Instagram',
    },
    {
      icon: Mail,
      href: 'mailto:loomagency55@gmail.com',
      label: 'Email',
    },
    {
      icon: Phone,
      href: 'tel:+351926132780',
      label: 'Telefone',
    },
  ];

  const navLinks = [
    { label: t.nav.about, href: '#sobre' },
    { label: t.nav.services, href: '#servicos' },
    { label: t.nav.clients, href: '#clientes' },
    { label: t.nav.form, href: '/form', isPage: true },
    { label: t.nav.contact, href: '#contacto' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer 
      data-testid="footer"
      className="bg-[#242328] text-[#f8f6e2] py-16 md:py-20"
    >
      <div className="container-main">
        {/* Top section */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          {/* Logo & tagline */}
          <div className="md:col-span-5 text-center md:text-left">
            <Link 
              to="/"
              className="inline-block mb-4"
            >
              <img 
                src="/logo-loom-white.png" 
                alt="Loom Studio" 
                className="h-28 md:h-36 w-auto"
              />
            </Link>
            <p className="text-[#f8f6e2]/60 leading-relaxed max-w-sm">
              {t.footer.tagline}
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-3 text-center md:text-left">
            <h4 className="font-['Syne'] text-sm font-semibold uppercase tracking-wider text-[#f8f6e2]/40 mb-6">
              {t.footer.navigation}
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  {link.isPage ? (
                    <Link
                      to={link.href}
                      data-testid={`footer-nav-${link.label.toLowerCase()}`}
                      className="group inline-flex items-center gap-2 text-[#f8f6e2]/70 hover:text-[#ff00c7] transition-colors duration-200"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" strokeWidth={1.5} />
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      data-testid={`footer-nav-${link.label.toLowerCase()}`}
                      className="group inline-flex items-center gap-2 text-[#f8f6e2]/70 hover:text-[#ff00c7] transition-colors duration-200"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" strokeWidth={1.5} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4 text-center md:text-left">
            <h4 className="font-['Syne'] text-sm font-semibold uppercase tracking-wider text-[#f8f6e2]/40 mb-6">
              {t.footer.contact}
            </h4>
            <div className="space-y-3 text-[#f8f6e2]/70">
              <p>loomagency55@gmail.com</p>
              <p>+351 926 132 780</p>
              <p>Tavira, Portugal</p>
            </div>
            
            {/* Social links */}
            <div className="flex gap-4 mt-6 justify-center md:justify-start">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith('http') ? '_blank' : undefined}
                  rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full border border-[#f8f6e2]/20 flex items-center justify-center hover:bg-[#ff00c7] hover:border-[#ff00c7] transition-colors duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" strokeWidth={1.5} />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-[#f8f6e2]/10 mb-8" />

        {/* Bottom section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#f8f6e2]/40">
          <p>© {currentYear} Loom Agency. {t.footer.rights}</p>
          <p>{t.footer.madeIn}</p>
        </div>
      </div>
    </footer>
  );
};
